import { ArrowDown, ArrowUp, CheckCircle2, Mail, MessageSquareText, Plus, Save, Send, Sparkles, Trash2, XCircle } from 'lucide-react';
import { Link, useLocation } from '../../app/router';
import './system-testing.css';
import { useEffect, useState } from 'react';
import { apiRequest, ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';

type AuditResponse = Record<string, unknown> & { sent?: boolean; audit_id?: string; tested_at?: string; provider?: string };
type HomeAdaptiveBlock = {
  id: string;
  title_th: string;
  title_en: string;
  subtitle_th: string;
  subtitle_en: string;
  query: string;
  icon: string;
  active: boolean;
};
type SystemSettings = Record<string, unknown> & { home_adaptive_blocks?: HomeAdaptiveBlock[] };

const emptyAdaptiveBlock = (): HomeAdaptiveBlock => ({
  id: `home_${Date.now()}`,
  title_th: '',
  title_en: '',
  subtitle_th: '',
  subtitle_en: '',
  query: '',
  icon: 'search',
  active: true,
});

function responseFromError(reason: unknown): AuditResponse {
  if (reason instanceof ApiError) return { sent: false, status: reason.status, code: reason.code, message: reason.message };
  return { sent: false, message: reason instanceof Error ? reason.message : 'Unknown error' };
}

function AuditResult({ result }: { result: AuditResponse | null }) {
  if (!result) return <div className="system-test-empty">Response จะแสดงที่นี่หลังส่ง</div>;
  const sent = result.sent === true;
  return <section className={`system-test-response ${sent ? 'is-success' : 'is-failed'}`} aria-live="polite">
    <header>{sent ? <CheckCircle2 /> : <XCircle />}<div><strong>{sent ? 'Delivery accepted' : 'Delivery failed'}</strong><small>{String(result.provider || 'backend')} · {String(result.tested_at || 'now')}</small></div>{result.audit_id && <code>{result.audit_id}</code>}</header>
    <pre>{JSON.stringify(result, null, 2)}</pre>
  </section>;
}

export function SystemTestingPage() {
  const { session } = useAuth();
  const { search } = useLocation();
  const requestedSection = new URLSearchParams(search).get('section');
  const section = requestedSection === 'home' || requestedSection === 'email' ? requestedSection : 'sms';
  const adminId = session?.expert_id || '';
  const [mail, setMail] = useState({ email: '', subject: 'beaw.club Mailing Test', message: 'beaw.club mailing service is alive.' });
  const [sms, setSms] = useState({ phone: '', message: 'beaw.club SMS service is alive.' });
  const [mailResult, setMailResult] = useState<AuditResponse | null>(null);
  const [smsResult, setSmsResult] = useState<AuditResponse | null>(null);
  const [smsSender, setSmsSender] = useState('');
  const [smsPreviewError, setSmsPreviewError] = useState('');
  const [smsPreviewVersion, setSmsPreviewVersion] = useState(0);
  useEffect(() => {
    const retry = () => setSmsPreviewVersion(value => value + 1);
    window.addEventListener('focus', retry);
    window.addEventListener('online', retry);
    return () => { window.removeEventListener('focus', retry); window.removeEventListener('online', retry); };
  }, []);
  useEffect(() => {
    if (!adminId || section !== 'sms') return;
    let active = true;
    setSmsSender(''); setSmsPreviewError('');
    apiRequest<{ sender: string }>('/admin/system-settings/sms-preview-config', { query: { admin_id: adminId } })
      .then(result => { if (active) { setSmsSender(result.sender); setSmsPreviewError(''); } })
      .catch(reason => {
        if (!active) return;
        const status = reason instanceof ApiError ? reason.status : 0;
        setSmsPreviewError(status === 404 ? 'Backend ยังไม่มี API Preview กรุณาลองใหม่เมื่ออัปเดตแล้ว (404)'
          : status === 401 || status === 403 ? `ไม่มีสิทธิ์โหลด Sender กรุณาตรวจสอบบัญชี Admin (${status})`
          : `โหลด Sender ไม่สำเร็จ${status ? ` (${status})` : ' กรุณาตรวจสอบการเชื่อมต่อ'}`);
      });
    return () => { active = false; };
  }, [adminId, section, smsPreviewVersion]);

  const [sending, setSending] = useState<'email' | 'sms' | ''>('');
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [blocks, setBlocks] = useState<HomeAdaptiveBlock[]>([]);
  const [blockStatus, setBlockStatus] = useState('');
  const [savingBlocks, setSavingBlocks] = useState(false);
  useEffect(() => {
    let active = true;
    apiRequest<SystemSettings>('/admin/system-settings')
      .then((payload) => {
        if (!active) return;
        setSettings(payload);
        setBlocks(Array.isArray(payload.home_adaptive_blocks) ? payload.home_adaptive_blocks : []);
      })
      .catch((reason) => active && setBlockStatus(responseFromError(reason).message?.toString() || 'โหลด config ไม่สำเร็จ'));
    return () => { active = false; };
  }, []);
  const updateBlock = (index: number, patch: Partial<HomeAdaptiveBlock>) => {
    setBlocks((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  };
  const moveBlock = (index: number, offset: number) => {
    setBlocks((current) => {
      const nextIndex = index + offset;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };
  const saveBlocks = async () => {
    if (!settings) return;
    const invalid = blocks.find((item) => !item.title_th.trim() || !item.query.trim());
    if (invalid) { setBlockStatus('ทุก block ต้องมีหัวข้อภาษาไทยและคำค้น'); return; }
    setSavingBlocks(true); setBlockStatus('');
    try {
      const saved = await apiRequest<SystemSettings>('/admin/system-settings', {
        method: 'PUT',
        body: { ...settings, home_adaptive_blocks: blocks },
      });
      setSettings(saved); setBlocks(saved.home_adaptive_blocks || blocks); setBlockStatus('บันทึก Adaptive Home blocks แล้ว');
    } catch (reason) { setBlockStatus(responseFromError(reason).message?.toString() || 'บันทึกไม่สำเร็จ'); }
    finally { setSavingBlocks(false); }
  };
  const sendMail = async (event: React.FormEvent) => {
    event.preventDefault(); setSending('email'); setMailResult(null);
    try { setMailResult(await apiRequest<AuditResponse>('/admin/system-settings/test-email', { method: 'POST', body: { ...mail, admin_id: adminId } })); }
    catch (reason) { setMailResult(responseFromError(reason)); }
    finally { setSending(''); }
  };
  const sendSms = async (event: React.FormEvent) => {
    event.preventDefault(); setSending('sms'); setSmsResult(null);
    try { setSmsResult(await apiRequest<AuditResponse>('/admin/system-settings/test-sms', { method: 'POST', body: { ...sms, admin_id: adminId } })); }
    catch (reason) { setSmsResult(responseFromError(reason)); }
    finally { setSending(''); }
  };
  return <WorkspaceLayout kind="admin" title="System Testing" description="เลือกส่วนที่ต้องการตั้งค่าหรือทดสอบจากเมนูย่อย">
    <nav className="system-testing-menu" aria-label="เมนูย่อย System Testing">
      {[{ id: 'sms', label: 'SMS Test', Icon: MessageSquareText }, { id: 'email', label: 'Email Test', Icon: Mail }, { id: 'home', label: 'Home Search Blocks', Icon: Sparkles }].map(({ id, label, Icon }) =>
        <Link key={id} to={`/admin/system/testing?section=${id}`} className={`button ${section === id ? 'button--primary' : 'button--ghost'}`}><Icon size={18} />{label}</Link>)}
      <Link to="/admin/system/transactions" className="button button--ghost">Delivery Log</Link>
    </nav>
    <div className="system-testing-content">
    <article className="panel adaptive-block-admin" hidden={section !== 'home'}>
      <header className="adaptive-block-heading"><span><Sparkles /></span><div><p className="eyebrow">ADAPTIVE HOME</p><h2>Home Search Blocks</h2><p>Block รายได้จะเป็นหน้าแรกเสมอ รายการด้านล่างจะ animate ต่อจากนั้นตามลำดับ</p></div></header>
      <div className="adaptive-block-list">
        {blocks.map((block, index) => <section className="adaptive-block-editor" key={block.id || index}>
          <div className="adaptive-block-toolbar"><strong>Block {index + 2}</strong><label><input type="checkbox" checked={block.active !== false} onChange={(event) => updateBlock(index, { active: event.target.checked })} /> เปิดใช้</label><button type="button" aria-label="เลื่อนขึ้น" onClick={() => moveBlock(index, -1)} disabled={index === 0}><ArrowUp /></button><button type="button" aria-label="เลื่อนลง" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}><ArrowDown /></button><button type="button" className="is-danger" aria-label="ลบ" onClick={() => setBlocks((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 /></button></div>
          <div className="adaptive-block-fields">
            <label className="field"><span>หัวข้อไทย</span><input value={block.title_th} onChange={(event) => updateBlock(index, { title_th: event.target.value })} placeholder="หาที่นั่งทำงาน" /></label>
            <label className="field"><span>English title</span><input value={block.title_en} onChange={(event) => updateBlock(index, { title_en: event.target.value })} placeholder="Find a place to work" /></label>
            <label className="field"><span>คำอธิบายไทย</span><input value={block.subtitle_th} onChange={(event) => updateBlock(index, { subtitle_th: event.target.value })} /></label>
            <label className="field"><span>English subtitle</span><input value={block.subtitle_en} onChange={(event) => updateBlock(index, { subtitle_en: event.target.value })} /></label>
            <label className="field"><span>คำค้นที่ส่งไป Search</span><input value={block.query} onChange={(event) => updateBlock(index, { query: event.target.value })} placeholder="ร้านอาหารสุขภาพ" /></label>
            <label className="field"><span>Icon</span><select value={block.icon} onChange={(event) => updateBlock(index, { icon: event.target.value })}><option value="search">Search</option><option value="coffee">Coffee</option><option value="work">Work</option><option value="kids">Kids</option><option value="healthy">Healthy</option><option value="dinner">Dinner</option><option value="nightlife">Nightlife</option></select></label>
          </div>
        </section>)}
      </div>
      <footer className="adaptive-block-actions"><button type="button" className="button" onClick={() => setBlocks((current) => [...current, emptyAdaptiveBlock()])}><Plus />สร้าง Block</button><button type="button" className="button button--primary" onClick={saveBlocks} disabled={!settings || savingBlocks}><Save />{savingBlocks ? 'กำลังบันทึก...' : 'บันทึกขึ้น Home'}</button>{blockStatus && <span>{blockStatus}</span>}</footer>
    </article>
    <div className="system-test-grid">
      <article className="panel system-test-card" hidden={section !== 'email'}>
        <header><span><Mail /></span><div><p className="eyebrow">EMAIL DELIVERY</p><h2>Mailing Test</h2></div></header>
        <form onSubmit={sendMail}>
          <label className="field"><span>Recipient email</span><input type="email" required value={mail.email} onChange={(event) => setMail({ ...mail, email: event.target.value })} placeholder="name@example.com" /></label>
          <label className="field"><span>Subject</span><input required maxLength={300} value={mail.subject} onChange={(event) => setMail({ ...mail, subject: event.target.value })} /></label>
          <label className="field"><span>Content</span><textarea required maxLength={10000} rows={6} value={mail.message} onChange={(event) => setMail({ ...mail, message: event.target.value })} /></label>
          <button className="button button--primary" disabled={Boolean(sending)}><Send />{sending === 'email' ? 'กำลังส่ง...' : 'ส่ง Mailing Test'}</button>
        </form>
        <AuditResult result={mailResult} />
      </article>
      <article className="panel system-test-card" hidden={section !== 'sms'}>
        <header><span><MessageSquareText /></span><div><p className="eyebrow">SMS DELIVERY</p><h2>SMS Test</h2></div></header>
        <form onSubmit={sendSms}>
          <label className="field"><span>Phone number</span><input type="tel" required minLength={6} maxLength={24} value={sms.phone} onChange={(event) => setSms({ ...sms, phone: event.target.value })} placeholder="08xxxxxxxx หรือ +668xxxxxxxx" /></label>
          <label className="field"><span>Content</span><textarea required maxLength={1000} rows={6} value={sms.message} onChange={(event) => setSms({ ...sms, message: event.target.value })} /></label>
          <section className="panel" aria-label="SMS simulation" aria-live="polite">
            <strong>SMS Preview · ยังไม่ส่งจริง</strong>
            <dl><dt>Sender (จาก backend)</dt><dd>{smsSender || smsPreviewError || 'กำลังโหลด...'} {smsPreviewError && <button type="button" className="button button--ghost" onClick={() => setSmsPreviewVersion(value => value + 1)}>ลองโหลด Sender อีกครั้ง</button>}</dd>
              <dt>To</dt><dd>{sms.phone.trim() || '—'}</dd>
              <dt>Message</dt><dd style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{sms.message.trim() || '—'}</dd></dl>
          </section>
          <button className="button button--primary" disabled={Boolean(sending) || !smsSender || !sms.message.trim()}><Send />{sending === 'sms' ? 'กำลังส่ง...' : 'ส่ง SMS Test'}</button>
        </form>
        <AuditResult result={smsResult} />
      </article>
    </div>
    </div>
  </WorkspaceLayout>;
}
